import React from "react";
import { motion } from "framer-motion";

const products = [
  {
    tag: "SIG-01",
    name: "Merkle Ledger",
    tagline: "The proof layer.",
    body:
      "A ledger primitive that binds every commercial event to a tamper-evident Merkle root. Anchored, signed, and independently verifiable in seconds.",
    metrics: [
      { k: "Anchor", v: "Ed25519 + Merkle-DAG" },
      { k: "Throughput", v: "48k ev/s per node" },
      { k: "Verifier", v: "Public / Auditor" },
    ],
    image:
      "https://images.unsplash.com/photo-1644088379091-d574269d422f?auto=format&fit=crop&w=1400&q=70",
    clip: "clip-frame-1",
  },
  {
    tag: "SIG-02",
    name: "Inter-ERP Commerce",
    tagline: "The bridge.",
    body:
      "A ledger-signed messaging bus between SAP, Oracle, MS Dynamics and Odoo. Purchase orders, invoices and shipments become verifiable inter-company events.",
    metrics: [
      { k: "Connectors", v: "SAP · Oracle · Dynamics · Odoo" },
      { k: "Latency", v: "<180ms end-to-end" },
      { k: "Format", v: "UBL · EDI · JSON" },
    ],
    image:
      "https://images.unsplash.com/photo-1738737271801-d404a575d870?auto=format&fit=crop&w=1400&q=70",
    clip: "clip-frame-2",
  },
  {
    tag: "SIG-03",
    name: "IoT Audit Automation",
    tagline: "The witness.",
    body:
      "Firmware SDK + gateway that signs sensor events at the edge — every temperature, weight, meter tick, or cycle becomes a ledger-anchored fact for the auditor.",
    metrics: [
      { k: "Edge", v: "ARM / RISC-V" },
      { k: "Attestation", v: "TPM 2.0 · Secure Element" },
      { k: "Audit", v: "Continuous / Zero-touch" },
    ],
    image:
      "https://images.pexels.com/photos/7102037/pexels-photo-7102037.jpeg?auto=compress&cs=tinysrgb&w=1400",
    clip: "clip-frame-3",
  },
];

export const Portfolio = () => {
  return (
    <section
      id="portfolio"
      className="relative py-28 md:py-40 bg-ink-900"
      data-testid="portfolio-section"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="flex items-baseline justify-between border-b border-white/10 pb-6 mb-16">
          <div className="font-mono text-[11px] tracking-[0.35em] uppercase text-spectrum-green">
            ◢ Software Portfolio
          </div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
            03 signatures
          </div>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-white mb-20 max-w-4xl"
        >
          Three products. <span className="spectrum-text">One ledger.</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {products.map((p, i) => (
            <motion.article
              key={p.tag}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.08 * i, ease: [0.22, 1, 0.36, 1] }}
              className={`relative group border border-white/10 bg-ink-950 overflow-hidden ${
                i === 0 ? "lg:col-span-7" : i === 1 ? "lg:col-span-5" : "lg:col-span-12"
              }`}
              data-testid={`portfolio-card-${p.tag.toLowerCase()}`}
            >
              <div className={`grid ${i === 2 ? "grid-cols-1 lg:grid-cols-12" : "grid-cols-1"}`}>
                <div
                  className={`${i === 2 ? "lg:col-span-6 order-2 lg:order-1" : ""} relative aspect-[16/10] overflow-hidden ${p.clip}`}
                >
                  <img
                    src={p.image}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover brightness-75 contrast-125 grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms] ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-spectrum-violet/20 via-transparent to-spectrum-cyan/20 mix-blend-overlay opacity-50 group-hover:opacity-100 transition-opacity duration-700" />
                  <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.3em] uppercase text-spectrum-green bg-ink-950/70 backdrop-blur px-2 py-1 border border-white/10">
                    {p.tag}
                  </div>
                </div>
                <div className={`${i === 2 ? "lg:col-span-6 order-1 lg:order-2" : ""} p-8 md:p-10`}>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3">
                    {p.tagline}
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight tracking-tight mb-5">
                    {p.name}
                  </h3>
                  <p className="text-white/60 text-base font-light leading-relaxed max-w-lg mb-8">
                    {p.body}
                  </p>
                  <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-white/10 pt-6">
                    {p.metrics.map((m) => (
                      <div key={m.k}>
                        <dt className="font-mono text-[9px] tracking-[0.3em] uppercase text-white/40 mb-1">
                          {m.k}
                        </dt>
                        <dd className="font-mono text-xs md:text-sm text-white">{m.v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
